import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  IconChevronRight,
  IconChevronLeft,
  IconChevronDown,
  IconHomeShare,
  IconCurrencyDollar,
  IconGitCompare,
  IconCalculator,
} from "@tabler/icons-react";
import classNames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import CountriesSelect, { CountriesType } from "../CountriesSelect/CountriesSelect";

import { NavSectionConfig, MegaMenuContent, NavLink } from "../../types/navbar";

type Props = {
  onAction: React.MouseEventHandler<HTMLButtonElement> | undefined;
  isDark?: boolean;
  countries: CountriesType[];
  navConfig: NavSectionConfig[];
  iconLinks: NavLink[];
};

// ── Sub-panel: content for a single section ───────────────────────────────────
const SectionPanel = ({
  section,
  content,
  onBack,
  onClose,
}: {
  section: NavSectionConfig;
  content: MegaMenuContent | null;
  onBack: () => void;
  onClose: () => void;
}) => {
  const [openCols, setOpenCols] = useState<Set<string>>(new Set());

  const toggleCol = (title: string) =>
    setOpenCols((prev) => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });

  return (
    <div className="px-6 pt-10 pb-10 h-full overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      {/* Header */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-white py-4 mb-4"
      >
        <IconChevronLeft size={20} />
        <span className="text-lg font-semibold">{section.title}</span>
      </button>

      {content ? (
        <>
          {/* Columns — each expandable */}
          <ul className="flex flex-col">
            {content.columns.map((col) => {
              const isOpen = openCols.has(col.title);
              return (
                <li key={col.title} className="border-b border-[#005230]">
                  <button
                    type="button"
                    onClick={() => toggleCol(col.title)}
                    className="w-full flex items-center justify-between py-4"
                  >
                    <span className="text-white text-lg font-semibold">{col.title}</span>
                    <IconChevronDown
                      size={20}
                      className={classNames("text-white transition-transform duration-200", isOpen && "rotate-180")}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.ul
                        className="pb-4 flex flex-col gap-3 overflow-hidden"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                      >
                        {col.links.map((item) => (
                          <li key={item.link}>
                            <Link
                              to={item.link}
                              onClick={onClose}
                              className="text-white text-base hover:text-white transition-colors"
                            >
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>

          {/* Footer link */}
          {content.footerLink && (
            <Link
              to={content.footerLink.link}
              onClick={onClose}
              className="inline-block mt-14 text-white underline underline-offset-1 text-base font-medium hover:text-white/80 transition-colors"
            >
              {content.footerLink.name}
            </Link>
          )}

          {/* Tools */}
          {content.tools && content.tools.length > 0 && (
            <div className="mt-14">
              <p className="text-white font-semibold text-[18px] mb-4">Herramientas</p>
              <ul className="flex flex-col gap-4">
                {content.tools.map((tool) => (
                  <li key={tool.link}>
                    <Link to={tool.link} onClick={onClose} className="flex items-center gap-3 group">
                      {tool.fallbackIcon === "comparator"
                        ? <IconGitCompare size={24} className="text-primary shrink-0" />
                        : <IconCalculator size={24} className="text-primary shrink-0" />
                      }
                      <span className="text-white text-base group-hover:text-white transition-colors">
                        {tool.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        /* Fallback: simple link list when no mega menu content */
        <ul className="flex flex-col">
          {section.links.map((item) => (
            <li key={item.link} className="border-b border-[#005230]">
              <Link
                to={item.link}
                onClick={onClose}
                className="flex py-4 text-white text-lg font-semibold hover:text-white/80 transition-colors"
              >
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const MobileMenu: React.FC<Props> = ({ countries, onAction, navConfig, iconLinks: rawIconLinks }) => {
  const { id = "", code } = useParams();
  const [activeSection, setActiveSection] = useState<NavSectionConfig | null>(null);
  const [openInline, setOpenInline] = useState<Set<string>>(new Set());
  const toggleInline = (title: string) =>
    setOpenInline((prev) => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });

  useEffect(() => {
    document.body.style.overflowY = "hidden";
    return () => { document.body.style.overflowY = ""; };
  }, []);

  const close = () => (onAction as any)?.();

  const allSections = navConfig;

  const ICON_MAP: Record<string, React.ReactNode> = {
    "Conviértete en propietario": <IconHomeShare size={22} className="text-primary" />,
    "Refiere y gana":             <IconCurrencyDollar size={22} className="text-primary" />,
  };
  const iconLinks = rawIconLinks.map((item: NavLink) => ({ ...item, icon: ICON_MAP[item.name] }));

  const activeMegaContent = activeSection?.megaMenu ?? null;

  return (
    <div className="relative w-full h-full bg-[#002918] rounded-l-[2rem] overflow-hidden" style={{ zIndex: 1000 }}>
      {/* ── Main panel ── */}
      <div
        className={classNames(
          "absolute inset-0 transition-transform duration-300 ease-in-out",
          activeSection ? "-translate-x-full" : "translate-x-0"
        )}
      >
        <div className="px-6 pt-10 pb-10 h-full overflow-y-auto">
          <motion.ul
            className="flex flex-col"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
          >
            {allSections.map((section) => {
              const expandable = section.mobileExpand;
              const inlineOpen = openInline.has(section.title);
              return (
                <motion.li
                  key={section.title}
                  className="border-b border-[#005230]"
                  variants={{
                    hidden: { opacity: 0, x: -16 },
                    visible: { opacity: 1, x: 0, transition: { duration: 0.25, ease: "easeOut" } },
                  }}
                >
                  {expandable ? (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleInline(section.title)}
                        className="w-full flex items-center justify-between py-4"
                      >
                        <span className="text-white text-lg font-semibold">{section.title}</span>
                        <IconChevronDown
                          size={20}
                          className={classNames("text-white transition-transform duration-200", inlineOpen && "rotate-180")}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {inlineOpen && (
                          <motion.ul
                            className="pb-4 flex flex-col gap-3 overflow-hidden"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                          >
                            {section.links.map((item) => (
                              <li key={item.link}>
                                <Link to={item.link} onClick={close} className="text-white text-base pl-1 hover:text-white transition-colors">
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActiveSection(section)}
                      className="w-full flex items-center justify-between py-4"
                    >
                      <span className="text-white text-lg font-semibold">{section.title}</span>
                      <IconChevronRight size={20} className="text-white" />
                    </button>
                  )}
                </motion.li>
              );
            })}
          </motion.ul>

          <ul className="flex flex-col gap-6 mt-14">
            {iconLinks.map((item) => (
              <li key={item.link}>
                <Link to={item.link} onClick={close} className="flex items-center gap-3">
                  {item.icon}
                  <span className="text-white text-[18px] font-medium">{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex flex-col mt-14 gap-6">
            {!id && !code && (
              <CountriesSelect mode="dark" countries={countries} dropdownAlign="left" mobile />
            )}
          </div>
        </div>
      </div>

      {/* ── Sub panel ── */}
      <div
        className={classNames(
          "absolute inset-0 transition-transform duration-300 ease-in-out",
          activeSection ? "translate-x-0" : "translate-x-full"
        )}
      >
        {activeSection && (
          <SectionPanel
            section={activeSection}
            content={activeMegaContent}
            onBack={() => setActiveSection(null)}
            onClose={close}
          />
        )}
      </div>
    </div>
  );
};

export default MobileMenu;
